// Типы для window.__ENV__
declare global {
  interface Window {
    __ENV__?: {
      VITE_API_URL?: string;
      VITE_BILLING_URL?: string;
      VITE_BILING_URL?: string;
      VITE_BILLING_MODE?: string;
      VITE_USER_URL?: string;
      VITE_WS_URL_CONVERSATIONS?: string;
      VITE_WS_URL_WORKSPACES?: string;
      VITE_WS_URL_NOTIFICATIONS?: string;
      VITE_WS_URL_MEETING_PROTOCOLS?: string;
      VITE_WS_URL_TEMPLATE_PROTOCOLS?: string;
      VITE_AUTH_PROVIDER?: string;
      VITE_KEYCLOAK_URL?: string;
      VITE_KEYCLOAK_REALM?: string;
      VITE_KEYCLOAK_CLIENT_ID?: string;
      /** Имя query-параметра, из которого читать JWT (по умолчанию token) */
      VITE_AUTH_TOKEN_QUERY_PARAM?: string;
      VITE_CHECK_BALANCE?: string;
      VITE_SHOW_CHAT?: string;
      VITE_SHOW_CHAT_INTEGRATIONS?: string;
      VITE_SHOW_LOGOUT_BUTTON?: string;
      VITE_SHOW_SUPPORT_CENTER?: string;
      VITE_COMPANY_INVITE_BY_DISPLAY_NAME_ENABLED?: string;
    };
  }
}

/**
 * Получает значение переменной окружения из window.__ENV__ (runtime) или import.meta.env (build-time)
 * @param key - Ключ переменной окружения
 * @param defaultValue - Значение по умолчанию, если переменная не найдена
 * @returns Значение переменной окружения
 */
export const getEnv = (key: string, defaultValue: string = ''): string => {
  // Сначала проверяем window.__ENV__ (runtime конфигурация)
  if (typeof window !== 'undefined' && window.__ENV__) {
    const value = window.__ENV__[key as keyof typeof window.__ENV__];
    // Проверяем, что значение не является плейсхолдером (не было заменено)
    if (value && value !== `{{${key}}}`) {
      return value;
    }
  }

  // Fallback на import.meta.env (build-time конфигурация)
  const buildTimeValue = import.meta.env[key];
  if (buildTimeValue) {
    return buildTimeValue;
  }

  return defaultValue;
};

/**
 * Объект с типизированными переменными окружения
 */
export const env = {
  VITE_API_URL: getEnv('VITE_API_URL', 'http://localhost:18000'),
  VITE_BILLING_URL: getEnv('VITE_BILLING_URL', getEnv('VITE_BILING_URL', '')),
  VITE_BILING_URL: getEnv('VITE_BILING_URL', getEnv('VITE_BILLING_URL', '')),
  VITE_BILLING_MODE: getEnv('VITE_BILLING_MODE', ''),
  VITE_USER_URL: getEnv('VITE_USER_URL', 'https://api.flowai.ru/auth-api/v1.0'),
  VITE_WS_URL_CONVERSATIONS: getEnv(
    'VITE_WS_URL_CONVERSATIONS',
    'ws://localhost:18000/ws/conversation',
  ),
  VITE_WS_URL_WORKSPACES: getEnv('VITE_WS_URL_WORKSPACES', 'ws://localhost:18000/ws/workspaces'),
  VITE_WS_URL_NOTIFICATIONS: getEnv(
    'VITE_WS_URL_NOTIFICATIONS',
    'wss://protocolist-server.flowai.ru/ws/notifications',
  ),
  VITE_WS_URL_MEETING_PROTOCOLS: getEnv(
    'VITE_WS_URL_MEETING_PROTOCOLS',
    'wss://protocolist-server.flowai.ru/ws/meeting-protocols/v1',
  ),
  VITE_WS_URL_TEMPLATE_PROTOCOLS: getEnv(
    'VITE_WS_URL_TEMPLATE_PROTOCOLS',
    'wss://t-api.rnd.flowai.ru/ws/template-protocols/v1',
  ),
  VITE_AUTH_PROVIDER: getEnv('VITE_AUTH_PROVIDER', 'keycloak') as
    | '3d'
    | 'keycloak'
    | '3d-only-query',
  VITE_KEYCLOAK_URL: getEnv('VITE_KEYCLOAK_URL', 'http://localhost:8082'),
  VITE_KEYCLOAK_REALM: getEnv('VITE_KEYCLOAK_REALM', 'meet'),
  VITE_KEYCLOAK_CLIENT_ID: getEnv('VITE_KEYCLOAK_CLIENT_ID', 'protocolist-frontend'),
  /** Имя query-параметра для JWT при редиректе после логина (?token=... или ?jwt=...) */
  VITE_AUTH_TOKEN_QUERY_PARAM: getEnv('VITE_AUTH_TOKEN_QUERY_PARAM', 'token'),
  VITE_CHECK_BALANCE: getEnv('VITE_CHECK_BALANCE', 'false'),
  VITE_SHOW_CHAT: getEnv('VITE_SHOW_CHAT', 'false'),
  VITE_SHOW_CHAT_INTEGRATIONS: getEnv('VITE_SHOW_CHAT_INTEGRATIONS', 'false'),
  VITE_SHOW_LOGOUT_BUTTON: getEnv('VITE_SHOW_LOGOUT_BUTTON', 'true'),
  VITE_SHOW_SUPPORT_CENTER: getEnv('VITE_SHOW_SUPPORT_CENTER', 'true'),
  VITE_COMPANY_INVITE_BY_DISPLAY_NAME_ENABLED: getEnv(
    'VITE_COMPANY_INVITE_BY_DISPLAY_NAME_ENABLED',
    'false',
  ),
};

export const getBillingServiceBaseUrl = (): string => {
  if (env.VITE_BILLING_MODE === 'self_hosted') {
    return '';
  }

  return getEnv('VITE_BILLING_URL', getEnv('VITE_BILING_URL', ''));
};

export const isSelfHostedDeployment = (): boolean => {
  if (env.VITE_BILLING_MODE === 'self_hosted') {
    return true;
  }

  if (env.VITE_BILLING_MODE === 'saas') {
    return false;
  }

  return env.VITE_AUTH_PROVIDER !== 'keycloak';
};
